import { css } from '@emotion/react';
import { urls } from '@zapier/identity-utils';
import { Link, atoms } from '@zapier/marketing-ui';
import { addParamsToUrl } from '@zapier/url-utils';
import { Invitation } from '../../hooks';
import { useLoginWithSAMLSSO } from '../../hooks/useLoginWithSAMLSSO';
import { QueryParams } from '../../types';
import { EmailForm } from './EmailForm';

export type LoginWithSAMLSSOFormProps = {
  /**
   * Current query params (so they can be preserved when routing)
   */
  queryParams?: QueryParams;

  /**
   * Email to prefill in form
   */
  defaultEmail?: string;

  /**
   * Forces users to log in to email account associated with invitation
   */
  invitation?: Invitation | null;

  /**
   * Url of page to redirect user to after successfully logging in
   */
  next?: string;

  /**
   * Routing callback to integrate with router. Will use window.href.location directly when not specified
   */
  routeTo?: (url: string) => void;
};

const FORM_ERROR_ID = 'sso-form-error';

const rootStyle = css`
  display: grid;
  grid-gap: 20px;
`;

// The auth column is shrink-to-fit on mobile; the error wraps in it instead of widening it.
const formErrorStyle = css`
  contain: inline-size;
`;

export function LoginWithSAMLSSOForm({
  queryParams,
  defaultEmail,
  invitation,
  next,
  routeTo,
}: LoginWithSAMLSSOFormProps) {
  const { canSubmit, handleSubmit, handleChange, formErrors, status, email } =
    useLoginWithSAMLSSO({
      defaultEmail: defaultEmail,
      email: invitation?.email,
      next: next,
      routeTo: routeTo,
    });

  // A disabled (invitation) field can't take focus, so its error is announced in the region.
  const isEmailFieldDisabled = Boolean(invitation?.email);
  const isFormLevelError = Boolean(status === 'error' && formErrors.error);
  const announcedError =
    status === 'error'
      ? formErrors.error ||
        (isEmailFieldDisabled ? formErrors.email : undefined)
      : undefined;

  return (
    <section css={rootStyle}>
      <EmailForm
        canSubmit={canSubmit}
        email={email}
        formErrorId={announcedError ? FORM_ERROR_ID : undefined}
        formErrors={formErrors}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        shouldFocusEmailField
        status={status}
        shouldDisableEmailField={isEmailFieldDisabled}
      />
      {/* Always mounted, empty: a live region must exist before its message. */}
      <div
        css={formErrorStyle}
        className={
          isFormLevelError
            ? `${atoms.bodySM} ${atoms.textError} ${atoms.textCenter}`
            : atoms.srOnly
        }
        role="alert"
        id={FORM_ERROR_ID}
      >
        {announcedError ?? null}
      </div>
      <p
        className={`${atoms.bodySM} ${atoms.textNeutral800} ${atoms.textCenter}`}
      >
        Does your company not have SSO?{' '}
        <Link href={addParamsToUrl(urls.LOGIN_URL, queryParams)}>
          Return to login.
        </Link>
      </p>
    </section>
  );
}
